import React, {
  useEffect, useMemo, useContext, useCallback
} from 'react';
import {
  string, bool, number, shape, arrayOf
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { useClickInteractionListener } from '@thd-olt-functional/utils/';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  PodRow,
  ProductBadge,
  ProductImage,
  PodFooter,
  PodSection,
  PodSpacer,
  PodTapper,
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { dataModel } from './RetailMediaCarouselPod.dataModel';
import { isSafari } from '../../../utils';
import {
  useRetailMediaCarouselPodTelemetry,
  useProductPodRedirectUrl,
  useNodeRef,
} from '../../../hooks';

const RetailMediaCarouselPod = ({
  product, storeId, position, noATCFulfillment, showAtc, impressionHeadline, plaLocation
}) => {
  const experienceContext = useContext(ExperienceContext);

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();
  const [productPodRatingRef, productPodRatingNode] = useNodeRef();

  // --
  const isMobile = useMemo(
    () => experienceContext.channel === 'mobile',
    [experienceContext.channel]
  );

  // --
  const { redirectUrl } = useProductPodRedirectUrl({
    canonicalUrl: product?.identifiers?.canonicalUrl
  });

  // -----------------
  // Telemetry
  // -----------------
  const {
    triggerClickOnAtcButton,
    triggerClickOnImage,
    triggerClickOnHeader,
    triggerClickOnRating,
    triggerClickOnPod,
    triggerProductPodV7Click,
    triggerClickOnNewTab
  } = useRetailMediaCarouselPodTelemetry({
    productPodNode,
    itemId: product?.itemId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    position,
    slotId: product?.info?.sponsoredMetadata?.slotId
  });

  const sendAnalytics = () => {
    triggerProductPodV7Click(); // analytic & ad-server
    triggerClickOnNewTab(); // new-relic
  };

  // -----------------
  // New-Tab Tracker
  // -----------------

  useClickInteractionListener({
    callbackFn: sendAnalytics,
    targetNode: productPodNode,
    itemId: product?.itemId,
    sponsoredId: product?.info?.sponsoredMetadata?.sponsoredId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    slotId: product?.info?.sponsoredMetadata?.slotId,
    position,
    plaLocation
  });

  // Utility function to handle modifier (cmd/ctrl/shift) keys check
  const modifierClick = (event) => {
    return (event.metaKey || event.ctrlKey || event.shiftKey);
  };

  // -----------------
  // Handlers
  // -----------------
  /**
   * Handler on: atc 'click'
   */
  const handleClickOnProductAtc = useCallback(() => {
    triggerClickOnAtcButton(); // new-relic
  }, [triggerClickOnAtcButton]);

  /**
   * Handler on: image 'click'
   */
  const handleClickOnProductImage = useCallback((event) => {
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnImage(); // new-relic
    window.location = redirectUrl; // redirect
  }, [redirectUrl, triggerClickOnImage]);

  /**
   * Handler on: header 'click'
   */
  const handleClickOnProductHeader = useCallback((event) => {
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnHeader(); // new-relic
    window.location = redirectUrl; // redirect
  }, [redirectUrl, triggerClickOnHeader]);

  /**
   * Handler on: rating 'click'
   */
  useEffect(() => {
    // check
    if (!productPodRatingNode) return undefined; // case: not mounted

    const selector = `a[href*="${product?.itemId}"]`;
    const element = productPodRatingNode.querySelector(selector);

    // check
    if (!element) return undefined;

    // --
    const handler = (event) => {
      if (modifierClick(event)) return;
      event.preventDefault();
      // --
      triggerClickOnRating(); // new-relic
      window.location = redirectUrl; // redirect
    };

    // install
    element.addEventListener('click', handler);
    // uninstall
    return () => {
      element.removeEventListener('click', handler);
    };
  }, [productPodRatingNode, redirectUrl, product?.itemId, triggerClickOnRating]);
  // ---- ----

  /**
   * Handler on: pod 'click'
   */
  const handleClick = useCallback((event) => {
    if (modifierClick(event)) return;

    event.preventDefault();
    // --

    // Note: The following trigger call should be removed from this point once the
    //       product-pod tapper gets the same trigger implemented on it, otherwise
    //       the click will be triggered twice.

    // --
    triggerProductPodV7Click(); // analytic & ad-server

    // --
    triggerClickOnPod(); // new-relic
    window.location = redirectUrl; // redirect

  }, [redirectUrl, triggerClickOnPod]);

  // -----------------
  // ProductPod
  // -----------------

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={product.itemId}
      className="sui-flex sui-w-full sui-bg-primary"
      data-testid="sponsored-product-pod"
    >
      <ImpressionProvider
        data={{
          id: '',
          name: impressionHeadline || 'SponsoredProducts',
          component: 'SponsoredCarousel',
          type: 'sponsoredproduct',
          category: ''
        }}
      >
        <ProductPod
          itemId={product.itemId}
          storeId={storeId}
          padding="sui-p-0"
          gap="sui-gap-0"
          analyticsData={{ parent: 'sponsored-carousel', position: position - 1 }}
          render={(pod) => (
            <>
              <a
                className="sui-top-0 sui-left-0 sui-absolute sui-w-full sui-h-full sui-z-1"
                href={redirectUrl}
                onClick={handleClick}
                aria-label="Link"
                data-testid="sponsored-product-pod__tapper"
              />
              <PodSpacer minHeight="40px">
                <PodRow noPadding>
                  <ProductBadge itemId={pod.itemId} storeId={storeId} />
                </PodRow>
              </PodSpacer>
              <PodTapper zIndex>
                <ProductImage
                  itemId={pod.itemId}
                  showSecondaryImage={pod.showSecondaryImage}
                  onClick={handleClickOnProductImage}
                />
              </PodTapper>
              <PodSection columnAlign alignTop noPadding>
                <div data-testid={impressionHeadline} style={{ height: `${isSafari() ? '89px' : '68px'}` }}>
                  <PodTapper fitWidth zIndex>
                    <ProductHeader
                      brand="inline"
                      brandFontSize="sm"
                      itemId={pod.itemId}
                      brandTitleMaxLine={1}
                      titleMaxLine={isMobile ? 3 : 2}
                      disableShopThisCollection
                      onClick={handleClickOnProductHeader}
                    />
                  </PodTapper>
                </div>
                <PodSpacer minHeight="26px">
                  <div ref={productPodRatingRef}>
                    <PodTapper fitWidth zIndex>
                      <ProductRating itemId={pod.itemId} onlyReviewCount />
                    </PodTapper>
                  </div>
                </PodSpacer>
                <PodTapper zIndex pointerNone aPointer buttonPointer spanPointer>
                  <PodSpacer minHeight="60px">
                    <Price
                      itemId={pod.itemId}
                      large={false}
                      storeId={storeId}
                      displayEachUom={false}
                      onSavingsCenterToggle={pod.onSavingsCenterToggle}
                      hideBadge
                      stackWasPrice
                    />
                  </PodSpacer>
                </PodTapper>
              </PodSection>
              {!isMobile && showAtc && (
                <PodFooter noPadding>
                  <PodSpacer minHeight="40px">
                    <PodTapper zIndex>
                      <ProductATC
                        itemId={pod.itemId}
                        storeId={storeId}
                        checkGeneric
                        outline
                        doNotShowChooseYourOptions
                        noATCFulfillment={noATCFulfillment}
                        onClick={handleClickOnProductAtc}
                      />
                    </PodTapper>
                  </PodSpacer>
                </PodFooter>
              )}
            </>
          )}
        />
      </ImpressionProvider>
    </div>
  );
};

RetailMediaCarouselPod.propTypes = {
  storeId: string.isRequired,
  product: shape({
    itemId: string.isRequired,
    identifiers: shape({
      canonicalUrl: string,
    }),
    info: shape({
      sponsoredBeacon: shape({
        onClickBeacons: arrayOf(string),
        onViewBeacons: arrayOf(string),
      }),
      sponsoredMetadata: shape({
        trackSource: string,
        campaignId: string,
        placementId: string,
        slotId: string,
        sponsoredId: string,
      }),
    }),
  }).isRequired,
  position: number.isRequired,
  noATCFulfillment: bool,
  showAtc: bool,
  impressionHeadline: string.isRequired,
  plaLocation: string
};

RetailMediaCarouselPod.defaultProps = {
  noATCFulfillment: null,
  showAtc: true,
  plaLocation: ''
};

RetailMediaCarouselPod.dataModel = dataModel;

export { RetailMediaCarouselPod };
